import React, { FC, useCallback, useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { ISelectOption } from 'components/forms/Selects'
import CustomerIcon from 'transfix-icons/build/CustomerIcon'
import { SingleSelect } from 'transfix-ui/components/SingleSelect'
import { ICustomer } from 'types/FMSGraphqlTypes'
import { CUSTOMER_LABELS_TYPE } from 'apps/fleet-planner/constants/loads'
import { IFilterInputProps } from 'apps/fleet-planner/pages/loads/FilterFacilityLocation'
import { CUSTOMER_LABELS } from 'apps/fleet-planner/pages/loads/constants'
import { ILoadFilters } from 'apps/fleet-planner/types'
import { selectLoadLabels } from 'apps/fleet-planner/utils/selectors'

const filterOptions = (c: Pick<ICustomer, 'id' | 'name'>[]): ISelectOption<string>[] =>
  c.map(({ id, name }) => ({ value: id, label: name || '' }))

const mapLabels = (l: CUSTOMER_LABELS_TYPE[] = []): ISelectOption<CUSTOMER_LABELS_TYPE>[] =>
  l.map((value: CUSTOMER_LABELS_TYPE) => ({ value, label: CUSTOMER_LABELS[value] }))

export interface IFilterCustomerProps
  extends IFilterInputProps<Pick<ILoadFilters, 'customerId' | 'customerLabels'>> {
  customerList: Pick<ICustomer, 'id' | 'name'>[]
}

export const FilterCustomer: FC<IFilterCustomerProps> = ({
  customerList: customers,
  onChange: onChangeProp,
  value: valueProp,
}) => {
  const labels = useSelector(selectLoadLabels)

  const [seenLabels, setSeenLabels] = useState<CUSTOMER_LABELS_TYPE[]>([])
  useEffect(() => setSeenLabels(prev => [...new Set([...prev, ...labels])]), [labels])

  const options = useMemo(
    () =>
      [...filterOptions(customers), ...mapLabels(seenLabels)]
        .sort((opt1, opt2) => opt1.label.localeCompare(opt2.label))
        .filter((value, idx, array) => array.findIndex(opt => opt.label === value.label) === idx),
    [customers, seenLabels]
  )

  const selectHandler = useCallback(
    (change?: ISelectOption<string>) => {
      const { value } = change || {}
      if (value === CUSTOMER_LABELS_TYPE.TARGET_DEDICATED) {
        onChangeProp({ customerId: undefined, customerLabels: [value] })
      } else {
        onChangeProp({ customerId: value, customerLabels: undefined })
      }
    },
    [onChangeProp]
  )

  const { customerId = '', customerLabels = [] } = valueProp || {}
  const selected = useMemo(() => {
    if (customerLabels.includes(CUSTOMER_LABELS_TYPE.TARGET_DEDICATED)) {
      return CUSTOMER_LABELS_TYPE.TARGET_DEDICATED
    }
    return customerId
  }, [customerId, customerLabels])

  return (
    <SingleSelect
      id='loads-customer-filter'
      dataTest='loads-customer-filter'
      icon={<CustomerIcon size='large' />}
      label='Customer'
      options={options}
      onSelect={selectHandler}
      selected={selected}
      emptyStateLabel='No saved customers'
    />
  )
}
